/**
 * Session security utilities for enhanced authentication protection
 */

import { Session } from '@supabase/supabase-js';
import { logSecurityEvent } from './security';
import { securityConfig } from '@/config/security';

// Session validation helpers
export const validateSession = (session: Session | null): boolean => {
  if (!session || !session.access_token || !session.expires_at) {
    return false;
  }
  
  const expiresAt = new Date(session.expires_at * 1000);
  const now = new Date();
  const fiveMinutesFromNow = new Date(now.getTime() + (5 * 60 * 1000));
  
  // If expiring soon, warn but allow; Supabase auto-refresh handles renewal
  if (expiresAt <= fiveMinutesFromNow) {
    logSecurityEvent('session_expiring_soon', { 
      expiresAt: expiresAt.toISOString(),
      timeUntilExpiry: expiresAt.getTime() - now.getTime()
    });
    return true;
  }
  
  return true;
};

// Session activity tracking
export const updateLastActivity = (): void => {
  try {
    const now = new Date().toISOString();
    sessionStorage.setItem('last_activity', now);
  } catch (error) {
    console.warn('Could not update last activity timestamp:', error);
  }
};

export const getLastActivity = (): Date | null => {
  try {
    const stored = sessionStorage.getItem('last_activity');
    return stored ? new Date(stored) : null;
  } catch (error) {
    console.warn('Could not retrieve last activity timestamp:', error);
    return null;
  }
};

// Detect suspicious session activity
export const detectSuspiciousActivity = (session: Session | null): boolean => {
  if (!session?.user?.id) return false;
  
  try {
    const currentUserId = session.user.id;
    const storedUserId = sessionStorage.getItem('current_user_id');
    
    // Check for user ID mismatch (potential session hijacking)
    if (storedUserId && storedUserId !== currentUserId) {
      logSecurityEvent('session_user_mismatch', {
        storedUserId,
        currentUserId,
        suspiciousActivity: true
      });
      return true;
    }
    
    // Store current user ID for future checks
    sessionStorage.setItem('current_user_id', currentUserId);
    
    return false;
  } catch (error) {
    console.error('Error detecting suspicious activity:', error);
    return false;
  }
};

// Clear all session-related data
export const clearSessionData = (): void => {
  try {
    // Clear session storage
    const keysToRemove = [
      'last_activity',
      'current_user_id'
    ];
    
    keysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
    });
    
    // Clear profile cache
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('profile-') || key.startsWith('cached-')) {
        sessionStorage.removeItem(key);
      }
    });
    
    // Clear sensitive localStorage data
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('failed-login-attempts') || 
          key.startsWith('rate_limit_') || 
          key.startsWith('auth_')) {
        localStorage.removeItem(key);
      }
    });
    
    logSecurityEvent('session_data_cleared');
  } catch (error) {
    console.error('Error clearing session data:', error);
  }
};

// Generate secure session fingerprint
export const generateSessionFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Security fingerprint', 2, 2);
    }
    
    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenResolution: `${screen.width}x${screen.height}`,
      canvasFingerprint: canvas.toDataURL(),
      timestamp: Date.now()
    };
    
    // Create hash of fingerprint
    return btoa(JSON.stringify(fingerprint)).slice(0, 32);
  } catch (error) {
    console.warn('Could not generate session fingerprint:', error);
    return 'fallback-fingerprint';
  }
};

// Validate session fingerprint for additional security
export const validateSessionFingerprint = (storedFingerprint: string): boolean => {
  try {
    const currentFingerprint = generateSessionFingerprint();
    return storedFingerprint === currentFingerprint;
  } catch (error) {
    console.warn('Could not validate session fingerprint:', error);
    return true; // Fail open to avoid blocking legitimate users
  }
};

// Session timeout management
export const checkSessionTimeout = (maxInactiveMinutes: number = securityConfig.inactivityTimeoutMinutes): boolean => {
  const lastActivity = getLastActivity();
  if (!lastActivity) return false;
  
  const now = new Date();
  const timeSinceLastActivity = now.getTime() - lastActivity.getTime();
  const maxInactiveMs = maxInactiveMinutes * 60 * 1000;
  
  if (timeSinceLastActivity > maxInactiveMs) {
    logSecurityEvent('session_timeout', {
      lastActivity: lastActivity.toISOString(),
      timeSinceLastActivity,
      maxInactiveMs
    });
    return true;
  }
  
  return false;
};